import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import type { ICustomWidgetTypeProps } from "@swiggy-private/dls-react-mxn/dist/WidgetController/type";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { ActionType } from "@swiggy-private/dls-web-recipe-common-web/dist/components/CouponCard/types";

export enum CouponListingPLWidgetTypes {
    COUPON_CARD = "type.googleapis.com/swiggy.gandalf.widgets.v2.CouponSection",
    COUPON_TEXT_BANNER = "type.googleapis.com/swiggy.gandalf.widgets.v2.TextBanner",
    COUPON_SECTION_COMMS = "type.googleapis.com/swiggy.gandalf.widgets.v2.CouponSectionComms",
}

export interface MROCouponClashPopup {
    enabled: boolean;
    title: string;
    subtitle: string;
    secondaryCta: {
        text: string;
        onClick: VoidFunction;
    };
    primaryCta: {
        text: string;
        onClick: VoidFunction;
    };
    impressionEvent?: VoidFunction;
}

export enum CouponMessageCardCTAType {
    CTA_TYPE_APPLY = "CTA_TYPE_APPLY",
}

export enum CouponMessageCardType {
    ERROR_CARD_TYPE_CONFLICTING_OFFER_APPLIED = "ERROR_CARD_TYPE_CONFLICTING_OFFER_APPLIED",
}

export interface CouponMessageCard {
    icon: string;
    title: string;
    subtitle?: string;
    subtitleColor?: string;
    text?: string; // To be future-deprecated and replaced by: primarySavingsInfo
    subtext?: string; // To be future-deprecated and replaced by: primarySavingsInfo
    attributeValues?: Record<string, string>;
    description: string;
    confetti?: CouponConfetti;
    cta: {
        isCustomCTA?: boolean;
        text: string;
        color: string;
        textColor?: string;
        type?: CouponMessageCardCTAType;
    };
    secondaryDiscountCallout?: string; // To be future-deprecated and replaced by: secondarySavingsInfo
    /* TODO:
     * the following structure is same as having text and subtext,
     * but adding support so that text and subtext can be deprecated in future
     */

    primarySavingsInfo?: {
        text: string;
        subtext: string;
    };
    secondarySavingsInfo?: {
        text: string;
        subtext: string;
    };

    disclaimer?: string;
    /* eslint-disable @typescript-eslint/naming-convention */
    offer_card_type?: OfferCardType;
    card_footer?: CardFooter;
    type?: CouponMessageCardType;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export interface CardFooter {
    text: string;
    subtext: string;
    icon: string;
}

export enum OfferCardType {
    OFFER_CARD_TYPE_POCKET_HERO = "OFFER_CARD_TYPE_POCKET_HERO",
    OFFER_CARD_TYPE_MAX_SAVINGS = "OFFER_CARD_TYPE_MAX_SAVINGS",
}

export interface TradeDiscountSuccessCard {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    is_reward_offer: boolean;
    subtitle: string;
    savings: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    logo_id: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    extra_savings_callout?: [
        {
            text: string;
        },
    ];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    td_success_card_type?: TDSuccessCardType;
    title?: string;
    cta?: {
        text: string;
        color: keyof IColors;
    };
}

export enum TDSuccessCardType {
    OFFER_CARD_TYPE_INVALID = "OFFER_CARD_TYPE_INVALID",
    OFFER_CARD_TYPE_REWARD_OFFER = "OFFER_CARD_TYPE_REWARD_OFFER",
    OFFER_CARD_TYPE_VALUE_MEALS = "OFFER_CARD_TYPE_VALUE_MEALS",
    OFFER_CARD_TYPE_POCKET_HERO = "OFFER_CARD_TYPE_POCKET_HERO",
    OFFER_CARD_TYPE_FREEBIE_OFFER = "OFFER_CARD_TYPE_FREEBIE_OFFER",
    OFFER_CARD_TYPE_DEAL_RUSH = "OFFER_CARD_TYPE_DEAL_RUSH",
    OFFER_CARD_TYPE_REFERRAL_OFFER = "OFFER_CARD_TYPE_REFERRAL_OFFER",
    OFFER_CARD_TYPE_TOING_LOOT_DEAL = "OFFER_CARD_TYPE_TOING_LOOT_DEAL",
}

export const GenericTDSuccessCardType = [
    TDSuccessCardType.OFFER_CARD_TYPE_REFERRAL_OFFER,
    TDSuccessCardType.OFFER_CARD_TYPE_TOING_LOOT_DEAL,
];

export interface SavingsBanner {
    title: string;
    subtitle: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    message_details: Record<string, number>;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    header_savings_strip_presentation_config?: HeaderSavingsStripPresentationConfig;
}

export interface HeaderSavingsStripPresentationConfig {
    /* eslint-disable @typescript-eslint/naming-convention */
    rotated_animation_texts: Array<string>;
    animation_delay: number;
    show_shimmer: boolean;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export enum CouponConfetti {
    INVALID = 0,
    TOP_UP = 1,
    AUTO_APPLY = 2,
}

export enum ApplyCouponPotentialCommsType {
    INVALID = "INVALID",
    IPL_SWIGGY_SIXES = "SUPER_SIX_OFFER",
    FTUE = "FTUE",
}

export enum CouponTagIdentifiers {
    IPL_SUPER_SIX_OFFER = "SUPER_SIX_OFFER",
}

export interface ApplyCouponPotentialComms {
    icon: string;
    /* eslint-disable @typescript-eslint/naming-convention */
    background_color: string;
    type: ApplyCouponPotentialCommsType;
    text_color: string;
    text: string;
    closeIconColor?: string;
    closeIconSize?: number;
    onLoad?: VoidFunction;
    cloudinaryId?: string;
}

export interface ApplyCouponsWidgetInfo {
    restaurant_coupon?: ApplyCouponSuggestion;
    payment_coupon?: ApplyCouponSuggestion;
}

export interface ApplyCouponSuggestion {
    savings: string;
    extra_savings?: string;
    extra_saving_expiry_in_ms?: string;
    coupon_code: string;
    amount_to_add: string | null;
    comms: string | null;
    potential_coupon_info?: ApplyCouponPotentialComms;
    // mro child cart restaurant id added to back sync to main child cart object
    mroChildCartRestaurantId?: string | number;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export interface OfferTag {
    offerTag: string;
    textColor?: string;
    backgroundColor?: string;
}

export interface TnCInfo {
    title: string;
    bulletTexts?: string[];
}

export interface BenefitsInfoList {
    title: string;
    description?: string;
}

export interface BenefitsInfo {
    header?: string;
    benefitsList?: BenefitsInfoList[];
}

export interface RibbonUIMetaData {
    asset: {
        icon: string;
        height: number;
        width: number;
    };
    backgroundColor: string;
    textColor: keyof IColors;
}

export interface CouponTimer {
    text: string;
    endTimeEpochInSec: string | number;
    backgroundColor: string;
    textColor: keyof IColors;
}

export enum BUSINESS_LINES {
    GIFTING = "GIFTING",
    INSTAMART = "INSTAMART",
    FOOD = "FOOD",
}

export interface CouponInfo {
    id?: string;
    applicable: boolean;
    title?: string;
    subtitle?: string;
    emphasisedSubtitle?: string;
    description?: string;
    logo?: string;
    ribbonText?: string;
    ribbonUiMeta?: RibbonUIMetaData;
    tagIdentifiers: CouponTagIdentifiers[];
    titleTimer?: CouponTimer;
    validityText?: string;
    offerTag?: OfferTag;
    tnc?: TnCInfo;
    benefitsInfo?: BenefitsInfo;
    ctaText?: string;
    couponType?: string;
    businessLine?: BUSINESS_LINES;
    absoluteSavings?: number;
    isSelected?: boolean;
    isBestCoupon?: boolean;
}

export enum COUPON_GROUP_TYPES {
    MORE_OFFERS = "COUPON_SECTION_TYPE_MORE_OFFERS",
    PAYMENT_OFFERS = "COUPON_SECTION_TYPE_PAYMENT_OFFERS",
    BEST_COUPON = "COUPON_SECTION_TYPE_BEST_COUPONS",
    GREAT_DEALS = "COUPON_SECTION_TYPE_GREAT_DEALS",
    APPLIED_OFFERS = "COUPON_SECTION_TYPE_APPLIED_OFFERS",
    RECOMMENDED_OFFERS = "COUPON_SECTION_TYPE_RECOMMENDED_OFFERS",
}

export interface ICouponCardBanner {
    text: string;
    textColor: string;
    borderColor: string;
    bgColor: string[];
}

export interface IMetaInfo {
    maxCouponSelectionAllowedPerSection: number;
    couponSelectionStyle: ActionType;
    footerImage?: {
        assetId?: string;
        aspectRatio?: number;
    };
}

export interface ICouponCard extends ICustomWidgetTypeProps {
    "@type": CouponListingPLWidgetTypes.COUPON_CARD;
    title: string;
    type: COUPON_GROUP_TYPES;
    coupons?: CouponInfo[];
    message?: ICouponCardBanner;
    newSectionIndicator?: boolean;
    metaInfo?: IMetaInfo;
}

export interface ICouponCardTextBanner extends ICustomWidgetTypeProps {
    "@type": CouponListingPLWidgetTypes.COUPON_TEXT_BANNER;
    id?: string;
    widgetId?: string;
    text?: {
        title?: string;
    };
    bgColour?: string;
    iconUrl?: string;
}

export enum COUPON_CARD_COMMS_TYPES {
    HEADER_COMMS = "HEADER_COMMS",
    FOOTER_COMMS = "FOOTER_COMMS",
}

export interface IHeaderFooterComm {
    headerImage?: {
        icon: string;
        width: number;
        height: number;
    };
    baseTemplateText?: string;
    baseTemplateTextWithoutCount?: string;
    staticTemplateText?: string;
    staticTemplateTextWithoutCount?: string;
    staticSavingsText?: string;
}
export interface ICouponSectionComm extends ICustomWidgetTypeProps {
    "@type": CouponListingPLWidgetTypes.COUPON_SECTION_COMMS;
    type: COUPON_CARD_COMMS_TYPES.HEADER_COMMS | COUPON_CARD_COMMS_TYPES.FOOTER_COMMS;
    comms: IHeaderFooterComm;
}

export interface ICouponCards {
    card: { card: ICouponCard };
}

export interface ICouponSuccessResponse {
    statusMessage: string;
    cards: IPLWidgetControllerCards[];
    firstOffsetRequest?: boolean;
}

export interface INextBestCoupon {
    type: COUPON_GROUP_TYPES;
    coupon: CouponInfo;
}

export interface ICouponsAPIResponse {
    statusCode?: number;
    statusMessage?: string;
    success?: ICouponSuccessResponse;
}

export type ModifiedCouponElement = HTMLDivElement & {
    _id?: string;
    _couponType: string;
    _couponCode?: string;
    _isApplicable: boolean;
};

export interface CouponTimerPayload {
    isTimer: boolean;
    timerValue: string;
}

export interface IAppliedCouponCodes {
    businessLine: string;
    code: string;
}
